import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import ProspectGrid from '../../common/ProspectGrid';
import LoadingOverlay from '../../common/LoadingOverlay';
import { DateRangePicker } from 'react-dates';
import * as moment from 'moment';
import FormValidationDisplay from '../../common/form/ValidationDisplay';
import '../../../styles/FormDatePickerRange.css';
import { ProspectGridColumns, ProspectsGridColumns, TourGridColumns, TourSelectionOptions, TourStatuses } from '../../../utilities/Enums';
import { FlatProspect, getProspects, Prospect, ProspectInfo, ProspectTourInfo } from '../../../api/Prospect';
import { getJustProperties, Property } from '../../../api/Property';
import FormSelect from '../../common/form/FormSelect';
import { momentDateFormat } from '../../../utilities/formatting';
import { ProspectPopup } from './dashboardWidgets/prospectPopup';

type FocusedOptions = 'startDate' | 'endDate' | null;

const sortAsc = (
    firstValue: string,
    secondValue: string,
    sortColumn: ProspectGridColumns
): number => {
    if (firstValue === secondValue) {
        return 0;
    }
    switch (sortColumn) {
        case ProspectGridColumns.MoveInDate:
        case ProspectGridColumns.TourDate:
        case ProspectGridColumns.TimeSlot:
        case ProspectGridColumns.TourStart:
        case ProspectGridColumns.MoveInDate:
        case ProspectGridColumns.DateCreated:
            // date comparison
            return moment(firstValue).isBefore(secondValue) ? -1 : 1;
        default:
            // simple string compare
            return firstValue.toUpperCase() > secondValue.toUpperCase() ? 1 : -1;
    }
};

const sortProspectAsc = (
    firstValue: string,
    secondValue: string,
    sortColumn: ProspectsGridColumns
): number => {
    if (firstValue === secondValue) {
        return 0;
    }
    switch (sortColumn) {
        case ProspectsGridColumns.LastTourDate:
        case ProspectsGridColumns.InitialDate:
        case ProspectsGridColumns.Name:
        case ProspectsGridColumns.Email:
        case ProspectsGridColumns.Phone:
        case ProspectsGridColumns.LastTourPropertyName:
        case ProspectsGridColumns.Status:
        case ProspectsGridColumns.ApplicationStatus:
        case ProspectsGridColumns.TotalPropertiesVisited:

            // date comparison
            return moment(firstValue).isBefore(secondValue) ? -1 : 1;
        default:
            // simple string compare
            return firstValue.toUpperCase() > secondValue.toUpperCase() ? 1 : -1;
    }
};

const sortTourAsc = (
    firstValue: string,
    secondValue: string,
    sortColumn: TourGridColumns
): number => {
    if (firstValue === secondValue) {
        return 0;
    }
    switch (sortColumn) {
        case TourGridColumns.Name:
        case TourGridColumns.TourDate:
        case TourGridColumns.PropertyName:
            // date comparison
            return moment(firstValue).isBefore(secondValue) ? -1 : 1;
        default:
            // simple string compare
            return firstValue.toUpperCase() > secondValue.toUpperCase() ? 1 : -1;
    }
};

export const tourComparer = (
    sortColumn: TourGridColumns,
    sortDirection: 'ASC' | 'DESC'
) => {
    //TODO: need to check for sort
    return (a: ProspectTourInfo, b: ProspectTourInfo) => {
        let aValue: string;
        let bValue: string;
        switch (sortColumn) {
            case TourGridColumns.Name:
                aValue = `${a.firstName} ${a.lastName}`;
                bValue = `${b.firstName} ${b.lastName}`;
                break;
            case TourGridColumns.TourDate:
                aValue = a.tourStartDate;
                bValue = b.tourStartDate;
                break;
            default:
                aValue = a[sortColumn] as string;
                bValue = b[sortColumn] as string;
                break;
        }

        return sortDirection === 'ASC'
            ? sortTourAsc(aValue, bValue, sortColumn)
            : -sortTourAsc(aValue, bValue, sortColumn);
    };
};

export const prospectsComparer = (
    sortColumn: ProspectsGridColumns,
    sortDirection: 'ASC' | 'DESC'
) => {
    return (a: ProspectInfo, b: ProspectInfo) => {
        let aValue: string;
        let bValue: string;
        switch (sortColumn) {
            case ProspectsGridColumns.Name:
                aValue = `${a.firstName} ${a.lastName}`;
                bValue = `${b.firstName} ${b.lastName}`;
                break;
            case ProspectsGridColumns.InitialDate:
                aValue = a.initialDate;
                bValue = b.initialDate;
                break;
            case ProspectsGridColumns.LastTourDate:
                aValue = a.lastTourDate;
                bValue = b.lastTourDate;
                break;
            default:
                aValue = a[sortColumn] as string;
                bValue = b[sortColumn] as string;
                break;
        }

        return sortDirection === 'ASC'
            ? sortProspectAsc(aValue, bValue, sortColumn)
            : -sortProspectAsc(aValue, bValue, sortColumn);
    };
};

export const prospectComparer = (
    sortColumn: ProspectGridColumns,
    sortDirection: 'ASC' | 'DESC'
) => {
    return (a: FlatProspect, b: FlatProspect) => {
        let aValue: string;
        let bValue: string;
        switch (sortColumn) {
            case ProspectGridColumns.Name:
                aValue = `${a.firstName} ${a.lastName}`;
                bValue = `${b.firstName} ${b.lastName}`;
                break;
            case ProspectGridColumns.TourStart:
            case ProspectGridColumns.TourDate:
                aValue = a.tourInterval.startDateTime;
                bValue = b.tourInterval.startDateTime;
                break;
            default:
                aValue = a[sortColumn] as string;
                bValue = b[sortColumn] as string;
                break;
        }

        return sortDirection === 'ASC'
            ? sortAsc(aValue, bValue, sortColumn)
            : -sortAsc(aValue, bValue, sortColumn);
    };
};

interface State {
    flatProspects: FlatProspect[];
    prospects: Prospect[];
    isLoading: boolean;
    startDate: moment.Moment;
    endDate: moment.Moment;
    dateFocused: FocusedOptions;
    properties: Property[];
    propertyId?: number;
    errorMessage?: string;
    tourStatusFilter?: string;
    selectedProspectToken: string | null;
    selectedProspect?: Prospect;   
}

interface Props extends RouteComponentProps<{}> { }

class ProspectsSubPage extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            flatProspects: [],
            prospects: [],
            isLoading: true,
            startDate: moment(),
            endDate: moment().add(7, 'days'),
            dateFocused: null,
            properties: [],
            selectedProspectToken: null
        };

        this.handleDatesChange = this.handleDatesChange.bind(this);
        this.updateGrid = this.updateGrid.bind(this);
        this.handlePropertyChange = this.handlePropertyChange.bind(this);
        this.handleGridSort = this.handleGridSort.bind(this);
        this.updateSelectedProspectId = this.updateSelectedProspectId.bind(this);
        this.updateSelectedProspect = this.updateSelectedProspect.bind(this);
    }

    async componentDidMount() {
        window.alert(
            "The prospect tab will be deprecated by the end of this year. All of its existing functionality now exists in the dashboard tab."
        );

        let propertiesResult = await getJustProperties();
        if (propertiesResult.success && propertiesResult.data) {
            this.setState({
                properties: propertiesResult.data,
                propertyId:
                    propertiesResult.data.length > 0 ? propertiesResult.data[0].id : undefined
            });
        } else if (propertiesResult.error) {
            this.setState({
                errorMessage: propertiesResult.error
            });
        }

        if (propertiesResult.data && propertiesResult.data.length > 0) {
            await this.updateGrid(
                propertiesResult.data[0].id,
                this.state.startDate,
                this.state.endDate
            );
        }
    }

    render() {
        let filteredProspects =
            this.state.tourStatusFilter !== undefined &&
                this.state.tourStatusFilter !== TourSelectionOptions.All
                ? this.state.flatProspects.filter(prospect =>
                    this.state.tourStatusFilter === TourSelectionOptions.Excluded
                        ? prospect.excludeFromStatistics
                        : prospect.status === this.state.tourStatusFilter &&
                        !prospect.excludeFromStatistics
                )
                : this.state.flatProspects.filter(prospect => !prospect.excludeFromStatistics);

        return (
            <div className="position-relative">
                <h2 className="prospect-header">tour24 Prospects</h2>
                <hr className="prospect-line" />
                <div className="row">
                    <div className="col-12">
                        <LoadingOverlay show={this.state.isLoading} inBootstrapColumn={false} />
                        <div className="row">
                            <FormValidationDisplay errorMessage={this.state.errorMessage} />
                        </div>
                        <div className="row">
                            <div className="col-4 form-date-picker-range">
                                <DateRangePicker
                                    startDate={
                                        this.state.startDate ? moment(this.state.startDate) : null
                                    }
                                    endDate={this.state.endDate ? moment(this.state.endDate) : null}
                                    onDatesChange={this.handleDatesChange}
                                    focusedInput={this.state.dateFocused}
                                    onFocusChange={(focusedOptions: FocusedOptions) =>
                                        this.setState({ dateFocused: focusedOptions })
                                    }
                                    startDateId="prospects-start-date"
                                    endDateId="prospects-end-date"
                                    showClearDates={true}
                                    isOutsideRange={() => false}
                                    hideKeyboardShortcutsPanel={true}
                                />
                            </div>
                            <FormSelect
                                selectOptions={this.state.properties.map(property => ({
                                    value: property.id.toString(),
                                    label: property.name
                                }))}
                                selectProps={{
                                    id: 'propertyId',
                                    name: 'propertyId',
                                    required: true,
                                    value: this.state.propertyId ? this.state.propertyId : 0
                                }}
                                onChange={(inputName, newValue) =>
                                    this.handlePropertyChange(newValue ? (newValue as number) : 0)
                                }
                                className="col-4"
                            />
                            <FormSelect
                                selectOptions={[
                                    { value: 'All', label: 'All' },
                                    {
                                        value: TourStatuses.Scheduled,
                                        label: TourStatuses.Scheduled
                                    },
                                    {
                                        value: TourStatuses.Cancelled,
                                        label: TourStatuses.Cancelled
                                    },
                                    { value: TourStatuses.Complete, label: TourStatuses.Complete },
                                    { value: TourStatuses.Reminded, label: TourStatuses.Reminded },
                                    { value: TourStatuses.NoShow, label: TourStatuses.NoShow },
                                    {
                                        value: TourSelectionOptions.Applied,
                                        label: TourSelectionOptions.Applied
                                    },
                                    {
                                        value: TourSelectionOptions.Excluded,
                                        label: TourSelectionOptions.Excluded
                                    }
                                ]}
                                selectProps={{
                                    id: 'tourStatus',
                                    name: 'tourStatus',
                                    required: false,
                                    value: this.state.tourStatusFilter
                                        ? this.state.tourStatusFilter
                                        : 'All'
                                }}
                                onChange={(inputName, newValue) => {
                                    this.setState({
                                        tourStatusFilter: newValue as string
                                    });
                                }}
                                className="col-4"
                            />
                        </div>
                        <div className="row">
                            <div className="col-12">
                                <ProspectGrid
                                    rows={filteredProspects}
                                    onSort={this.handleGridSort}
                                    selectProspect={this.updateSelectedProspectId}
                                />
                                <ProspectPopup
                                    prospectToken={this.state.selectedProspectToken}
                                    prospect={this.state.selectedProspect}
                                    propertyId={
                                        this.state.propertyId === undefined
                                            ? null
                                            : this.state.propertyId
                                    }
                                    setSelectedProspectToken={this.updateSelectedProspectId}
                                    updateSelectedProspect={this.updateSelectedProspect}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    async updateGrid(propertyId: number, startDate: moment.Moment, endDate: moment.Moment) {
        this.setState({
            isLoading: true,
            errorMessage: undefined
        });
        let prospectResult = await getProspects({
            startDate: startDate.format(momentDateFormat),
            endDate: endDate.clone().add(1, 'day').format(momentDateFormat),
            propertyId: propertyId
        });
        if (prospectResult.success && prospectResult.data) {
            this.setState({
                prospects: prospectResult.data.filter(prospect => prospect.contactInfo),
                flatProspects: prospectResult.data
                    .filter(prospect => prospect.contactInfo)
                    .map(prospect => ({
                        ...prospect.contactInfo!,
                        ...prospect,
                        tourInterval: prospect.tourInfo.event!,
                        status: prospect.tourInfo.status
                    })),
                isLoading: false
            });
        } else if (prospectResult.error) {
            this.setState({
                errorMessage: prospectResult.error
            });
        }
        this.handleGridSort(ProspectGridColumns.TourDate, 'NONE');
    }

    async handleDatesChange(range: { startDate: moment.Moment; endDate: moment.Moment }) {
        this.setState({
            startDate: range.startDate,
            endDate: range.endDate,
            errorMessage: undefined
        });

        if (this.state.propertyId) {
            await this.updateGrid(this.state.propertyId, range.startDate, range.endDate);
        }
    }

    async handlePropertyChange(propertyId: number) {
        this.setState({
            ...this.state,
            propertyId: propertyId,
            errorMessage: undefined
        });

        await this.updateGrid(propertyId, this.state.startDate, this.state.endDate);
    }
    private handleGridSort(
        sortColumn: ProspectGridColumns,
        sortDirection: 'ASC' | 'DESC' | 'NONE'
    ) {
        this.setState(prevState => ({
            flatProspects: prevState.flatProspects.sort(
                sortDirection === 'NONE'
                    ? prospectComparer(ProspectGridColumns.TourDate, 'ASC')
                    : prospectComparer(sortColumn, sortDirection)
            )
        }));
    }

    private updateSelectedProspectId(id: string) {
        this.setState({
            ...this.state,
            selectedProspectToken: id,
            selectedProspect: this.state.prospects.find(p => p.publicKey === id)
        });
    }

    private updateSelectedProspect(newProspect: Prospect, cancelled: boolean) {
        let tempProspects = this.state.prospects;
        let tempFlatProspects = this.state.flatProspects;
        for (let i = 0; i < tempProspects.length; i++) {
            if (tempProspects[i].publicKey === newProspect.publicKey) {
                tempProspects[i] = newProspect;
                tempProspects[i].tourInfo.status = cancelled
                    ? TourStatuses.Cancelled
                    : tempProspects[i].tourInfo.status;
                tempFlatProspects[i].tourInterval = newProspect.tourInfo.event!;
                tempFlatProspects[i].excludeFromStatistics =
                    newProspect.contactInfo.excludeFromStatistics;
                tempFlatProspects[i].status = cancelled
                    ? TourStatuses.Cancelled
                    : tempProspects[i].tourInfo.status;
                break;
            }
        }
        this.setState({
            prospects: tempProspects,
            flatProspects: tempFlatProspects
        });
    }
}

export default withRouter<Props>(ProspectsSubPage);



// WEBPACK FOOTER //
// ./src/components/pages/admin/ProspectsSubPage.tsx